import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector, useStore } from 'react-redux';
import { Translate, withLocalize } from 'react-localize-redux';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import OverflowingText from '../../components/foundations/OverflowingText';
import Tooltip from '../../components/molecules/Tooltip';
import TopbarService from '../../services/topbar';
import toaster from '../../utils/toaster';
import { getUserData } from '../../selectors/user';
import { setActionType, updateBookmarkData } from '../../actions/reports';
import { updatedField } from '../../actions/fields';
import { initialStateBookmark } from '../../reducers/reports';
import {
    buildBookmarkOpenAction,
    buildBookmarkSavePayload,
    canUserDeleteBookmark,
    canUserEditBookmark,
    decodeBookmarkName,
    dispatchBookmarkEvent,
    getActiveBookmarkId,
    getBookmarkGroupsToShow,
    getBookmarkReportStateKey,
} from '../bookmarkUtils';
import {
    BOOKMARK_EVENT,
    BOOKMARK_GROUP_COLORS,
    BOOKMARK_GROUP_TRANSLATION_KEYS,
    BOOKMARK_TYPE_TO_GROUP,
    BOOKMARKS_PREFIX,
    HEADERS,
} from '../consts';
import topbarCss from '../style.css';
import css from '../savedReports.css';

const topbarService = new TopbarService();

const VIEW_PICKER = 'picker';
const VIEW_MANAGE = 'manage';
const TAB_ALL = 'all';
const TAB_MINE = 'mine';
const TAB_PUBLIC = 'public';
const FILTER_ALL = 'all';
const FILTER_MINE = 'mine';
const FILTER_PUBLIC = 'public';

const PICKER_TAB_COUNT_KEYS = {
    [TAB_ALL]: 'REPORTS_COUNT',
    [TAB_MINE]: 'MY_REPORTS_COUNT',
    [TAB_PUBLIC]: 'PUBLIC_REPORTS_COUNT',
};

const PICKER_TAB_EMPTY_KEYS = {
    [TAB_ALL]: 'NO_ALL_MATCHES',
    [TAB_MINE]: 'NO_MINE_MATCHES',
    [TAB_PUBLIC]: 'NO_PUBLIC_MATCHES',
};

const FAVORITES_STORAGE_PREFIX = 'topbar.savedReportsFavorites.';

const MAX_BOOKMARK_NAME_LENGTH = 100;

const TOAST_AUTO_CLOSE = 3000;

const loadFavoriteIds = email => {
    try {
        const stored = JSON.parse(window.localStorage.getItem(`${FAVORITES_STORAGE_PREFIX}${email}`));
        return Array.isArray(stored) ? stored : [];
    } catch (error) {
        return [];
    }
};

const storeFavoriteIds = (email, uuids) => {
    try {
        window.localStorage.setItem(`${FAVORITES_STORAGE_PREFIX}${email}`, JSON.stringify(uuids));
    } catch {}
};

const applyBookmarkMutation = (current, eventType, data) => {
    const uuid = eventType === 'bookmarkRemoved' ? data : data?.uuid;
    if (!uuid) return current;
    if (eventType === 'bookmarkRemoved') return current.filter(bookmark => bookmark.uuid !== uuid);
    if (eventType === 'bookmarkUpdated') {
        return current.map(bookmark => (bookmark.uuid === uuid ? data : bookmark));
    }
    if (eventType === 'bookmarkAdded') {
        return current.some(bookmark => bookmark.uuid === uuid)
            ? current.map(bookmark => (bookmark.uuid === uuid ? data : bookmark))
            : [...current, data];
    }
    return current;
};

const matchesQuery = (bookmark, query) => {
    if (!query) return true;
    const lowered = query.toLowerCase();
    return (
        decodeBookmarkName(bookmark.name)
            .toLowerCase()
            .includes(lowered) || (bookmark.creator || '').toLowerCase().includes(lowered)
    );
};

const groupSections = (groups, items) =>
    groups.map(group => ({ group, items: items.filter(item => item.group === group) })).filter(s => s.items.length);

function BookmarkName({ bookmark }) {
    return (
        <>
            {bookmark.impersonated_org && bookmark.impersonated_org_data && (
                <span>( {bookmark.impersonated_org_data.display_name} ) </span>
            )}
            {decodeBookmarkName(bookmark.name)}
        </>
    );
}

BookmarkName.propTypes = {
    bookmark: PropTypes.shape({
        name: PropTypes.string,
        impersonated_org: PropTypes.string,
        impersonated_org_data: PropTypes.shape({ display_name: PropTypes.string }),
    }).isRequired,
};

function SavedReportsPanel({ translate }) {
    const userData = useSelector(getUserData) || {};
    const store = useStore();
    const [open, setOpen] = useState(false);
    const [view, setView] = useState(VIEW_PICKER);
    const [bookmarks, setBookmarks] = useState([]);
    const [topBookmarkIds, setTopBookmarkIds] = useState([]);
    const [favoriteIds, setFavoriteIds] = useState([]);
    const [pickerQuery, setPickerQuery] = useState('');
    const [pickerTab, setPickerTab] = useState(TAB_ALL);
    const [pickerGroups, setPickerGroups] = useState([]);
    const [manageQuery, setManageQuery] = useState('');
    const [manageFilter, setManageFilter] = useState(FILTER_ALL);
    const [editing, setEditing] = useState(null);
    const [confirmDeleteUuid, setConfirmDeleteUuid] = useState(null);
    const [busy, setBusy] = useState(false);
    const loadSequence = useRef(0);
    const containerRef = useRef(null);
    const triggerRef = useRef(null);
    const searchInputRef = useRef(null);
    const renameInputRef = useRef(null);
    const renameCancelledRef = useRef(false);

    const loadBookmarks = () => {
        // Ignore stale responses from overlapping refreshes.
        const requestId = ++loadSequence.current;
        topbarService
            .getBookmarks()
            .then(({ bookmarks: loaded, top_bookmarks: top }) => {
                if (requestId !== loadSequence.current) return;
                setBookmarks(loaded || []);
                setTopBookmarkIds(top || []);
            })
            .catch(() => {
                if (requestId !== loadSequence.current) return;
                toaster.error(translate(`${BOOKMARKS_PREFIX}.LOAD_ERROR`), { autoClose: TOAST_AUTO_CLOSE });
            });
    };

    useEffect(() => {
        loadBookmarks();
        window.addEventListener(BOOKMARK_EVENT, loadBookmarks);
        return () => window.removeEventListener(BOOKMARK_EVENT, loadBookmarks);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (userData.email) {
            setFavoriteIds(loadFavoriteIds(userData.email));
        }
    }, [userData.email]);

    useEffect(() => {
        if (open) {
            searchInputRef.current?.focus();
        }
    }, [open, view]);

    useEffect(() => {
        if (editing) {
            renameInputRef.current?.focus();
            renameInputRef.current?.select();
        }
    }, [editing?.uuid]); // eslint-disable-line react-hooks/exhaustive-deps

    const openPanel = () => {
        setOpen(true);
        window.mixpanel.track('Bookmarks: Open Saved Reports', { reportCount: bookmarks.length });
    };

    const closePanel = () => {
        setOpen(false);
        setView(VIEW_PICKER);
        setEditing(null);
        setConfirmDeleteUuid(null);
        // Reset search, tabs, and type filters so the panel reopens in its default state.
        setPickerQuery('');
        setPickerTab(TAB_ALL);
        setPickerGroups([]);
        setManageQuery('');
        setManageFilter(FILTER_ALL);
        triggerRef.current?.focus();
    };

    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = event => {
            if (event.key === 'Escape') {
                closePanel();
            }
        };
        const onMouseDown = event => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                closePanel();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('mousedown', onMouseDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('mousedown', onMouseDown);
        };
    }, [open]);

    if (!userData.email || userData.should_block_app) {
        return null;
    }

    const isStaging = window.location.href.indexOf('staging.singular.net') > -1;

    const openBookmark = bookmark => {
        const action = buildBookmarkOpenAction(bookmark, {
            search: window.location.search,
            href: window.location.href,
        });
        if (!action) return;

        const bookmarkForLog = { ...bookmark };
        delete bookmarkForLog.url;
        window.mixpanel.track('Bookmarks: Open bookmark', {
            Bookmark: bookmarkForLog,
            BookmarkQuery: action.bookmarkQuery,
            reportType: bookmark.type,
        });

        if (!userData.is_impersonator || isStaging) {
            topbarService.recordBookmarkOpen(bookmark.uuid, bookmark.creator).catch(() => {});
        }

        closePanel();

        if (action.href) {
            window.location.href = action.href;
        } else if (action.reload) {
            window.location.hash = action.hash;
            window.location.reload();
        } else {
            window.location.hash = action.hash;
        }
    };

    const toggleFavorite = bookmark => {
        const next = bookmark.favorite
            ? favoriteIds.filter(uuid => uuid !== bookmark.uuid)
            : [...favoriteIds, bookmark.uuid];
        setFavoriteIds(next);
        storeFavoriteIds(userData.email, next);
        window.mixpanel.track(`Bookmarks: ${bookmark.favorite ? 'Unfavorite' : 'Favorite'} Report`, {
            Bookmark: bookmark,
            reportType: bookmark.type,
        });
    };

    const commitBookmarkMutation = (eventType, data) => {
        setBookmarks(current => applyBookmarkMutation(current, eventType, data));
        if (eventType === 'bookmarkRemoved') {
            setTopBookmarkIds(current => current.filter(uuid => uuid !== data));
        }

        // Keep Creative Gallery's shared list current while the API refresh is in flight.
        const fieldBookmarks = store.getState().fields?.bookmarks;
        if (fieldBookmarks) {
            store.dispatch(updatedField('bookmarks', applyBookmarkMutation(fieldBookmarks, eventType, data)));
        }
    };

    // Creative Gallery stores the full bookmark; other reports store a bookmark wrapper.
    const syncActiveReportState = (bookmark, updatedBookmark) => {
        const stateKey = getBookmarkReportStateKey(bookmark.type);
        const pageBookmark = store.getState()[stateKey]?.bookmark;
        if (!pageBookmark) return;
        const usesFullBookmark = !!pageBookmark.uuid;
        const activeBookmarkId = pageBookmark.bookmark_id || pageBookmark.uuid;
        if (activeBookmarkId !== bookmark.uuid) return;
        const data = usesFullBookmark
            ? updatedBookmark || initialStateBookmark
            : updatedBookmark
            ? { bookmark_id: bookmark.uuid, bookmark: updatedBookmark }
            : { bookmark_id: '' };
        store.dispatch(setActionType(updateBookmarkData(data), stateKey));
    };

    const saveMutation = ({ payload, mixpanelEvent, bookmark, eventType, successMessage, errorKey }) => {
        setBusy(true);
        window.mixpanel.track(mixpanelEvent, { Bookmark: bookmark, reportType: bookmark.type });
        topbarService
            .saveBookmark(payload)
            .then(response => {
                commitBookmarkMutation(eventType, response.bookmark);
                dispatchBookmarkEvent(eventType, response.bookmark);
                if (eventType === 'bookmarkUpdated') {
                    syncActiveReportState(bookmark, response.bookmark);
                }
                toaster.success(successMessage, { autoClose: TOAST_AUTO_CLOSE });
            })
            .catch(() => toaster.error(translate(`${BOOKMARKS_PREFIX}.${errorKey}`), { autoClose: TOAST_AUTO_CLOSE }))
            .finally(() => setBusy(false));
    };

    // User-provided bookmark names must not be rendered as HTML.
    const translatePlain = (key, data) => translate(key, data, { renderInnerHtml: false });

    const startRename = bookmark => {
        renameCancelledRef.current = false;
        setConfirmDeleteUuid(null);
        setEditing({ uuid: bookmark.uuid, value: decodeBookmarkName(bookmark.name) });
    };

    const finishRename = bookmark => {
        const cancelled = renameCancelledRef.current;
        renameCancelledRef.current = false;
        const value = (editing?.value || '').trim();
        setEditing(null);
        if (cancelled || !value || value === decodeBookmarkName(bookmark.name)) return;
        const nameTaken = bookmarks.some(
            other =>
                other.uuid !== bookmark.uuid &&
                other.creator === userData.email &&
                decodeBookmarkName(other.name) === value
        );
        if (nameTaken) {
            toaster.error(translatePlain(`${BOOKMARKS_PREFIX}.RENAME_DUPLICATE_ERROR`, { name: value }), {
                autoClose: TOAST_AUTO_CLOSE,
            });
            return;
        }
        saveMutation({
            payload: { ...buildBookmarkSavePayload(bookmark), name: value, id: bookmark.uuid },
            mixpanelEvent: 'Bookmarks: Rename Report',
            bookmark,
            eventType: 'bookmarkUpdated',
            successMessage: translatePlain(`${BOOKMARKS_PREFIX}.RENAMED_TOAST`, { name: value }),
            errorKey: 'RENAME_ERROR',
        });
    };

    const duplicateBookmark = bookmark => {
        const existingNames = new Set(
            bookmarks.filter(other => other.creator === userData.email).map(other => decodeBookmarkName(other.name))
        );
        const sourceName = decodeBookmarkName(bookmark.name);
        const suffixed = counter => {
            const suffix = ` ${translate(`${BOOKMARKS_PREFIX}.COPY_SUFFIX`)}${counter ? ` ${counter}` : ''}`;
            return `${sourceName.slice(0, MAX_BOOKMARK_NAME_LENGTH - suffix.length)}${suffix}`;
        };
        let name = suffixed(0);
        for (let counter = 2; existingNames.has(name); counter++) {
            name = suffixed(counter);
        }
        saveMutation({
            payload: { ...buildBookmarkSavePayload(bookmark), name, is_shared: false },
            mixpanelEvent: 'Bookmarks: Duplicate Report',
            bookmark,
            eventType: 'bookmarkAdded',
            successMessage: translatePlain(`${BOOKMARKS_PREFIX}.DUPLICATED_TOAST`, { name }),
            errorKey: 'DUPLICATE_ERROR',
        });
    };

    const toggleVisibility = bookmark => {
        const makeShared = !bookmark.is_shared;
        saveMutation({
            payload: { ...buildBookmarkSavePayload(bookmark), is_shared: makeShared, id: bookmark.uuid },
            mixpanelEvent: 'Bookmarks: Change Report Visibility',
            bookmark,
            eventType: 'bookmarkUpdated',
            successMessage: translatePlain(
                `${BOOKMARKS_PREFIX}.${makeShared ? 'MADE_PUBLIC_TOAST' : 'MADE_PRIVATE_TOAST'}`,
                { name: decodeBookmarkName(bookmark.name) }
            ),
            errorKey: 'VISIBILITY_ERROR',
        });
    };

    const deleteBookmark = bookmark => {
        setConfirmDeleteUuid(null);
        setBusy(true);
        window.mixpanel.track(`Bookmarks: Delete ${bookmark.type} Report`, {
            Bookmark: bookmark,
            BookmarkQuery: bookmark.url,
        });
        topbarService
            .deleteBookmark(bookmark.uuid)
            .then(() => {
                const remainingFavorites = favoriteIds.filter(uuid => uuid !== bookmark.uuid);
                setFavoriteIds(remainingFavorites);
                storeFavoriteIds(userData.email, remainingFavorites);
                commitBookmarkMutation('bookmarkRemoved', bookmark.uuid);
                dispatchBookmarkEvent('bookmarkRemoved', bookmark.uuid);
                syncActiveReportState(bookmark, null);
                toaster.success(
                    translate(
                        `${BOOKMARKS_PREFIX}.DELETED_TOAST`,
                        { name: decodeBookmarkName(bookmark.name) },
                        { renderInnerHtml: false }
                    ),
                    { autoClose: TOAST_AUTO_CLOSE }
                );
            })
            .catch(() => toaster.error(translate(`${BOOKMARKS_PREFIX}.DELETE_ERROR`), { autoClose: TOAST_AUTO_CLOSE }))
            .finally(() => setBusy(false));
    };

    const groupsToShow = getBookmarkGroupsToShow(userData.feature_flags);
    const enriched = bookmarks
        .map(bookmark => ({
            ...bookmark,
            group: BOOKMARK_TYPE_TO_GROUP[bookmark.type],
            mine: bookmark.creator === userData.email,
            favorite: favoriteIds.includes(bookmark.uuid),
        }))
        .filter(bookmark => groupsToShow.includes(bookmark.group));

    const activeBookmarkId = open ? getActiveBookmarkId() : null;

    const tabItems = enriched.filter(bookmark => {
        if (pickerTab === TAB_MINE) return bookmark.mine;
        if (pickerTab === TAB_PUBLIC) return bookmark.is_shared;
        return bookmark.mine || bookmark.is_shared;
    });
    const queriedTabItems = tabItems.filter(bookmark => matchesQuery(bookmark, pickerQuery));
    const pickerPool = pickerGroups.length
        ? queriedTabItems.filter(bookmark => pickerGroups.includes(bookmark.group))
        : queriedTabItems;
    const pickerSections = groupSections(groupsToShow, pickerPool);
    const chips = groupsToShow
        .filter(group => tabItems.some(bookmark => bookmark.group === group))
        .map(group => ({ group, count: tabItems.filter(bookmark => bookmark.group === group).length }));
    const favoriteList = enriched.filter(bookmark => bookmark.favorite);
    const frequentList = topBookmarkIds.map(uuid => enriched.find(bookmark => bookmark.uuid === uuid)).filter(Boolean);
    const pickerTabCountKey = PICKER_TAB_COUNT_KEYS[pickerTab];
    const pickerEmptyKey = bookmarks.length ? PICKER_TAB_EMPTY_KEYS[pickerTab] : 'EMPTY';

    const managePool = enriched
        .filter(bookmark => {
            if (manageFilter === FILTER_MINE) return bookmark.mine;
            if (manageFilter === FILTER_PUBLIC) return bookmark.is_shared;
            return true;
        })
        .filter(bookmark => matchesQuery(bookmark, manageQuery));
    const manageSections = groupSections(groupsToShow, managePool);

    const selectTab = tab => {
        setPickerTab(tab);
        setPickerGroups([]);
    };

    const toggleGroupChip = group =>
        setPickerGroups(current => (current.includes(group) ? current.filter(g => g !== group) : [...current, group]));

    const openManageView = () => {
        window.mixpanel.track('Bookmarks: Open Manage Reports', { reportCount: bookmarks.length });
        setView(VIEW_MANAGE);
    };

    const renderGroupHeader = section => (
        <div className={css.groupHeader}>
            <span className={css.groupDot} style={{ background: BOOKMARK_GROUP_COLORS[section.group]?.dot }} />
            <span className={css.groupTitle}>
                <Translate id={BOOKMARK_GROUP_TRANSLATION_KEYS[section.group]} />
            </span>
            <span className={css.groupCount}>{section.items.length}</span>
        </div>
    );

    const renderSidebarItem = bookmark => (
        <button
            key={bookmark.uuid}
            type="button"
            className={css.sidebarItem}
            title={decodeBookmarkName(bookmark.name)}
            onClick={() => openBookmark(bookmark)}
        >
            <span className={css.dot} style={{ background: BOOKMARK_GROUP_COLORS[bookmark.group]?.dot }} />
            <span className={css.sidebarItemName}>
                <BookmarkName bookmark={bookmark} />
            </span>
        </button>
    );

    const renderCard = bookmark => (
        <div
            key={bookmark.uuid}
            role="button"
            tabIndex={0}
            className={css.card}
            onClick={() => openBookmark(bookmark)}
            onKeyDown={event => {
                if (event.target !== event.currentTarget) return;
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openBookmark(bookmark);
                }
            }}
        >
            <button
                type="button"
                aria-label={translate(
                    `${BOOKMARKS_PREFIX}.${bookmark.favorite ? 'UNFAVORITE_TOOLTIP' : 'FAVORITE_TOOLTIP'}`
                )}
                className={css.starButton}
                onClick={event => {
                    event.stopPropagation();
                    toggleFavorite(bookmark);
                }}
            >
                <Icon
                    name={bookmark.favorite ? 'star-filled' : 'star'}
                    size={IconSizes.X_SMALL}
                    variant={bookmark.favorite ? IconVariants.WARNING : IconVariants.LIGHT}
                    interactive={false}
                />
            </button>
            <div className={css.cardBody}>
                <OverflowingText className={css.cardName}>
                    <BookmarkName bookmark={bookmark} />
                </OverflowingText>
                <div className={css.cardMeta}>
                    {bookmark.mine ? <Translate id={`${BOOKMARKS_PREFIX}.YOU`} /> : bookmark.creator}
                </div>
            </div>
            {bookmark.uuid === activeBookmarkId && (
                <span className={css.activeCheck} title={translate(`${BOOKMARKS_PREFIX}.ACTIVE_TOOLTIP`)}>
                    <Icon name="check" size={IconSizes.X_SMALL} variant={IconVariants.SUCCESS} interactive={false} />
                </span>
            )}
        </div>
    );

    const renderManageRow = bookmark => {
        const isEditing = editing?.uuid === bookmark.uuid;
        const isConfirming = confirmDeleteUuid === bookmark.uuid;
        // Saving a cross-org bookmark would rebind it to the current session's organization.
        const canEdit = canUserEditBookmark(bookmark, userData.email) && !bookmark.impersonated_org;
        const canDuplicate = !bookmark.impersonated_org;
        const canDelete = canUserDeleteBookmark(bookmark, userData.email);
        const ownedByTitle = translatePlain(`${BOOKMARKS_PREFIX}.OWNED_BY`, { owner: bookmark.creator });
        const renameTitle = canEdit ? translate(`${BOOKMARKS_PREFIX}.RENAME_TOOLTIP`) : ownedByTitle;
        const visibilityTitle = canEdit
            ? translate(`${BOOKMARKS_PREFIX}.${bookmark.is_shared ? 'MAKE_PRIVATE' : 'MAKE_PUBLIC'}`)
            : ownedByTitle;

        return (
            <div key={bookmark.uuid} className={css.row}>
                <span className={css.dot} style={{ background: BOOKMARK_GROUP_COLORS[bookmark.group]?.dot }} />
                <div className={css.rowBody}>
                    {isEditing ? (
                        <input
                            ref={renameInputRef}
                            className={css.renameInput}
                            type="text"
                            maxLength={MAX_BOOKMARK_NAME_LENGTH}
                            aria-label={translate(`${BOOKMARKS_PREFIX}.RENAME_TOOLTIP`)}
                            value={editing.value}
                            onChange={event => setEditing({ uuid: bookmark.uuid, value: event.target.value })}
                            onKeyDown={event => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    event.currentTarget.blur();
                                } else if (event.key === 'Escape') {
                                    event.stopPropagation();
                                    renameCancelledRef.current = true;
                                    event.currentTarget.blur();
                                }
                            }}
                            onBlur={() => finishRename(bookmark)}
                        />
                    ) : (
                        <>
                            <div className={css.cardName} title={decodeBookmarkName(bookmark.name)}>
                                <BookmarkName bookmark={bookmark} />
                            </div>
                            <div className={css.cardMeta}>
                                {bookmark.mine ? <Translate id={`${BOOKMARKS_PREFIX}.YOU`} /> : bookmark.creator}
                            </div>
                        </>
                    )}
                </div>
                <span className={classNames(css.visPill, bookmark.is_shared ? css.visPillPublic : css.visPillPrivate)}>
                    <Translate id={`${BOOKMARKS_PREFIX}.${bookmark.is_shared ? 'VIS_PUBLIC' : 'VIS_PRIVATE'}`} />
                </span>
                {isConfirming ? (
                    <div className={css.rowActions}>
                        <span className={css.confirmText}>
                            <Translate
                                id={`${BOOKMARKS_PREFIX}.${
                                    bookmark.is_shared ? 'DELETE_CONFIRM_SHARED' : 'DELETE_CONFIRM'
                                }`}
                            />
                        </span>
                        <button
                            type="button"
                            className={css.confirmCancelButton}
                            onClick={() => setConfirmDeleteUuid(null)}
                        >
                            <Translate id="STATIC.BUTTONS.CANCEL" />
                        </button>
                        <button
                            type="button"
                            className={css.confirmDeleteButton}
                            disabled={busy}
                            onClick={() => deleteBookmark(bookmark)}
                        >
                            <Translate id={`${BOOKMARKS_PREFIX}.DELETE`} />
                        </button>
                    </div>
                ) : (
                    <div className={css.rowActions}>
                        <button
                            type="button"
                            className={css.openButton}
                            disabled={busy}
                            onClick={() => openBookmark(bookmark)}
                        >
                            <Translate id={`${BOOKMARKS_PREFIX}.OPEN`} />
                        </button>
                        {canDuplicate && (
                            <Tooltip
                                title={translate(`${BOOKMARKS_PREFIX}.DUPLICATE_TOOLTIP`)}
                                style={{ display: 'flex' }}
                            >
                                <button
                                    type="button"
                                    aria-label={translate(`${BOOKMARKS_PREFIX}.DUPLICATE_TOOLTIP`)}
                                    className={css.rowIconButton}
                                    disabled={busy}
                                    onClick={() => duplicateBookmark(bookmark)}
                                >
                                    <Icon name="copy" size={IconSizes.X_SMALL} interactive={false} />
                                </button>
                            </Tooltip>
                        )}
                        {(canEdit || !bookmark.mine) && (
                            <>
                                <Tooltip title={renameTitle} style={{ display: 'flex' }}>
                                    <button
                                        type="button"
                                        aria-label={renameTitle}
                                        className={css.rowIconButton}
                                        disabled={busy || !canEdit}
                                        onClick={() => startRename(bookmark)}
                                    >
                                        <Icon name="pencil" size={IconSizes.X_SMALL} interactive={false} />
                                    </button>
                                </Tooltip>
                                <Tooltip title={visibilityTitle} style={{ display: 'flex' }}>
                                    <button
                                        type="button"
                                        aria-label={visibilityTitle}
                                        className={css.rowIconButton}
                                        disabled={busy || !canEdit}
                                        onClick={() => toggleVisibility(bookmark)}
                                    >
                                        <Icon
                                            name={bookmark.is_shared ? 'eye-off' : 'eye'}
                                            size={IconSizes.X_SMALL}
                                            interactive={false}
                                        />
                                    </button>
                                </Tooltip>
                            </>
                        )}
                        {canDelete && (
                            <Tooltip
                                title={translate(`${BOOKMARKS_PREFIX}.DELETE_TOOLTIP`)}
                                style={{ display: 'flex' }}
                            >
                                <button
                                    type="button"
                                    aria-label={translate(`${BOOKMARKS_PREFIX}.DELETE_TOOLTIP`)}
                                    className={classNames(css.rowIconButton, css.rowIconButtonDanger)}
                                    disabled={busy}
                                    onClick={() => setConfirmDeleteUuid(bookmark.uuid)}
                                >
                                    <Icon
                                        name="trash"
                                        size={IconSizes.X_SMALL}
                                        variant={IconVariants.ERROR}
                                        interactive={false}
                                    />
                                </button>
                            </Tooltip>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const pickerView = (
        <>
            <div className={css.header}>
                <div className={css.headerRow}>
                    <div className={css.searchBox}>
                        <Icon name="search" size={IconSizes.X_SMALL} variant={IconVariants.LIGHT} interactive={false} />
                        <input
                            ref={searchInputRef}
                            className={css.searchInput}
                            type="text"
                            autoComplete="off"
                            placeholder={translate(`${BOOKMARKS_PREFIX}.SEARCH_PLACEHOLDER`)}
                            value={pickerQuery}
                            onChange={event => setPickerQuery(event.target.value)}
                        />
                    </div>
                    <div className={css.segmented}>
                        {[
                            {
                                tab: TAB_ALL,
                                labelKey: 'ALL_TAB',
                                count: enriched.filter(b => b.mine || b.is_shared).length,
                            },
                            { tab: TAB_MINE, labelKey: 'MY_REPORTS_TAB', count: enriched.filter(b => b.mine).length },
                            {
                                tab: TAB_PUBLIC,
                                labelKey: 'PUBLIC_TAB',
                                count: enriched.filter(b => b.is_shared).length,
                            },
                        ].map(({ tab, labelKey, count }) => (
                            <button
                                key={tab}
                                type="button"
                                aria-pressed={pickerTab === tab}
                                className={classNames(css.segment, { [css.segmentActive]: pickerTab === tab })}
                                onClick={() => selectTab(tab)}
                            >
                                <Translate id={`${BOOKMARKS_PREFIX}.${labelKey}`} />
                                <span className={css.segmentCount}>{count}</span>
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        title={translate(`${BOOKMARKS_PREFIX}.CLOSE_TOOLTIP`)}
                        aria-label={translate(`${BOOKMARKS_PREFIX}.CLOSE_TOOLTIP`)}
                        className={css.squareButton}
                        onClick={closePanel}
                    >
                        <Icon name="x" size={IconSizes.SMALL} interactive={false} />
                    </button>
                </div>
                {!!chips.length && (
                    <div className={css.chipsRow}>
                        <button
                            type="button"
                            className={classNames(css.chip, { [css.chipAllActive]: !pickerGroups.length })}
                            onClick={() => setPickerGroups([])}
                        >
                            <Translate id={`${BOOKMARKS_PREFIX}.ALL_TYPES`} />
                        </button>
                        {chips.map(({ group, count }) => {
                            const colors = BOOKMARK_GROUP_COLORS[group] || {};
                            const active = pickerGroups.includes(group);
                            return (
                                <button
                                    key={group}
                                    type="button"
                                    className={classNames(css.chip, { [css.chipActive]: active })}
                                    style={
                                        active
                                            ? { background: colors.tint, color: colors.dot, borderColor: colors.dot }
                                            : undefined
                                    }
                                    onClick={() => toggleGroupChip(group)}
                                >
                                    <span className={css.dot} style={{ background: colors.dot }} />
                                    <Translate id={BOOKMARK_GROUP_TRANSLATION_KEYS[group]} />
                                    <span className={css.chipCount}>{count}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
            <div className={css.body}>
                {!!(favoriteList.length || frequentList.length) && (
                    <div className={css.sidebar}>
                        {!!favoriteList.length && (
                            <>
                                <div className={css.sidebarTitle}>
                                    <span className={css.sidebarStar}>
                                        <Icon
                                            name="star-filled"
                                            size={IconSizes.XX_SMALL}
                                            variant={IconVariants.WARNING}
                                            interactive={false}
                                        />
                                    </span>
                                    <Translate id={`${BOOKMARKS_PREFIX}.FAVORITES`} />
                                </div>
                                {favoriteList.map(renderSidebarItem)}
                            </>
                        )}
                        {!!frequentList.length && (
                            <>
                                <div
                                    className={classNames(css.sidebarTitle, {
                                        [css.sidebarTitleSpaced]: !!favoriteList.length,
                                    })}
                                >
                                    <Icon
                                        name="clock"
                                        size={IconSizes.XX_SMALL}
                                        variant={IconVariants.LIGHT}
                                        interactive={false}
                                    />
                                    <Translate id={`${BOOKMARKS_PREFIX}.FREQUENTLY_VIEWED`} />
                                </div>
                                {frequentList.map(renderSidebarItem)}
                            </>
                        )}
                    </div>
                )}
                <div className={css.gridArea}>
                    {pickerSections.map(section => (
                        <div key={section.group}>
                            {renderGroupHeader(section)}
                            <div className={css.cardsGrid}>{section.items.map(renderCard)}</div>
                        </div>
                    ))}
                    {!pickerSections.length && (
                        <div className={css.emptyState}>
                            <Translate id={`${BOOKMARKS_PREFIX}.${pickerEmptyKey}`} />
                        </div>
                    )}
                </div>
            </div>
            <div className={css.footer}>
                <span>
                    <span className={css.footerCountValue}>{pickerPool.length}</span>{' '}
                    <Translate id={`${BOOKMARKS_PREFIX}.${pickerTabCountKey}`} />
                </span>
                <button type="button" className={css.footerLink} onClick={openManageView}>
                    <Icon name="settings" size={IconSizes.X_SMALL} variant={IconVariants.ACTIVE} interactive={false} />
                    <Translate id={`${BOOKMARKS_PREFIX}.MANAGE`} />
                </button>
            </div>
        </>
    );

    const manageView = (
        <>
            <div className={css.header}>
                <div className={css.manageHeaderRow}>
                    <button
                        type="button"
                        title={translate(`${BOOKMARKS_PREFIX}.BACK_TOOLTIP`)}
                        aria-label={translate(`${BOOKMARKS_PREFIX}.BACK_TOOLTIP`)}
                        className={classNames(css.squareButton, css.backButton)}
                        onClick={() => setView(VIEW_PICKER)}
                    >
                        <Icon name="chevron-left" size={IconSizes.SMALL} interactive={false} />
                    </button>
                    <div className={css.manageTitle}>
                        <Translate id={`${BOOKMARKS_PREFIX}.MANAGE`} />
                    </div>
                    <button
                        type="button"
                        title={translate(`${BOOKMARKS_PREFIX}.CLOSE_TOOLTIP`)}
                        aria-label={translate(`${BOOKMARKS_PREFIX}.CLOSE_TOOLTIP`)}
                        className={classNames(css.squareButton, css.pushRight)}
                        onClick={closePanel}
                    >
                        <Icon name="x" size={IconSizes.SMALL} interactive={false} />
                    </button>
                </div>
                <div className={css.manageControls}>
                    <div className={css.searchBox}>
                        <Icon name="search" size={IconSizes.X_SMALL} variant={IconVariants.LIGHT} interactive={false} />
                        <input
                            ref={searchInputRef}
                            className={css.searchInput}
                            type="text"
                            autoComplete="off"
                            placeholder={translate(`${BOOKMARKS_PREFIX}.MANAGE_SEARCH_PLACEHOLDER`)}
                            value={manageQuery}
                            onChange={event => setManageQuery(event.target.value)}
                        />
                    </div>
                    <div className={css.segmented}>
                        {[
                            { filter: FILTER_ALL, labelKey: 'ALL_TAB' },
                            { filter: FILTER_MINE, labelKey: 'MY_REPORTS_TAB' },
                            { filter: FILTER_PUBLIC, labelKey: 'PUBLIC_TAB' },
                        ].map(({ filter, labelKey }) => (
                            <button
                                key={filter}
                                type="button"
                                aria-pressed={manageFilter === filter}
                                className={classNames(css.segment, { [css.segmentActive]: manageFilter === filter })}
                                onClick={() => setManageFilter(filter)}
                            >
                                <Translate id={`${BOOKMARKS_PREFIX}.${labelKey}`} />
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <div className={css.manageList}>
                {manageSections.map(section => (
                    <div key={section.group}>
                        {renderGroupHeader(section)}
                        <div className={css.rowsList}>{section.items.map(renderManageRow)}</div>
                    </div>
                ))}
                {!manageSections.length && (
                    <div className={css.emptyState}>
                        <Translate id={`${BOOKMARKS_PREFIX}.NO_MANAGE_MATCHES`} />
                    </div>
                )}
            </div>
            <div className={css.footer}>
                <span>
                    <span className={css.footerCountValue}>{managePool.length}</span>{' '}
                    <Translate id={`${BOOKMARKS_PREFIX}.REPORTS_COUNT`} />
                </span>
                <button type="button" className={css.footerLink} onClick={() => setView(VIEW_PICKER)}>
                    <Translate id={`${BOOKMARKS_PREFIX}.DONE`} />
                </button>
            </div>
        </>
    );

    return (
        <div className={topbarCss.menuContainer} ref={containerRef}>
            <button
                ref={triggerRef}
                type="button"
                aria-label="saved reports"
                aria-expanded={open}
                className={classNames(topbarCss.menuToggle, { [css.triggerOpen]: open })}
                onClick={() => (open ? closePanel() : openPanel())}
            >
                <Translate id={`${HEADERS}.BOOKMARKS`} />
                <span className={classNames(css.chevron, { [css.chevronOpen]: open })}>
                    <Icon name="chevron-down" size={IconSizes.MEDIUM} variant={IconVariants.WHITE} />
                </span>
            </button>
            {open && (
                <>
                    <div className={css.backdrop} role="presentation" onClick={closePanel} />
                    <div className={css.panel} role="dialog" aria-label={translate(`${HEADERS}.BOOKMARKS`)}>
                        {view === VIEW_PICKER ? pickerView : manageView}
                    </div>
                </>
            )}
        </div>
    );
}

SavedReportsPanel.propTypes = {
    translate: PropTypes.func.isRequired,
};

export default withLocalize(SavedReportsPanel);
